import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { transactions as fallback } from "../data/transactions.js";
import { parameters, assetClasses } from "../data/credit.js";
import { fetchTransactions, statusClass } from "../lib/cms.js";

const MONTHS = ["january","february","march","april","may","june","july","august","september","october","november","december"];

// Deal_Closed is free text in the CMS ("August 2026", "July 2024 and follow on rounds", "2021").
// Sort newest first by year, then month, then record id.
function closedKey(t) {
  const txt = String(t.dealClosed || t.year || "").toLowerCase();
  const y = (txt.match(/(20\d{2})/) || [])[1];
  const m = MONTHS.findIndex((name) => txt.includes(name));
  return [y ? Number(y) : 0, m >= 0 ? m + 1 : 0, Number(t.id) || 0];
}
function newestFirst(a, b) {
  const ka = closedKey(a), kb = closedKey(b);
  for (let i = 0; i < 3; i++) if (ka[i] !== kb[i]) return kb[i] - ka[i];
  return 0;
}

export default function Transactions() {
  const [list, setList] = useState(null);
  const [source, setSource] = useState("loading");

  useEffect(() => {
    fetchTransactions().then((data) => {
      if (data && data.length) {
        setList([...data].sort(newestFirst));
        setSource("cms");
      } else {
        setList(fallback.map((t, i) => ({ id: i, ...t })));
        setSource("fallback");
      }
    });
  }, []);

  return (
    <>
      <section className="page-header">
        <div className="wrap">
          <div className="eyebrow">Track record</div>
          <h1>Selected transactions across the Daksfirst book.</h1>
          <p className="sub">
            A selection of credit and structured transactions Daksfirst has originated, closed
            or exited across the UK, India, UAE and Frontier Markets. Full transaction memos
            and the live pipeline are available in the data room under NDA.
          </p>
        </div>
      </section>

      {/* TRANSACTION CARDS */}
      <section>
        <div className="wrap">
          {list === null && (
            <p className="muted" style={{ padding: "32px 0" }}>Loading transactions…</p>
          )}

          {list && list.length === 0 && (
            <p className="muted" style={{ padding: "32px 0" }}>
              No transactions to display. Reach the team at{" "}
              <a href="mailto:info@daksfirst.com" style={{ color: "var(--gold)" }}>
                info@daksfirst.com
              </a>.
            </p>
          )}

          {list && list.length > 0 && (
            <>
              <div className="tx-grid">
                {list.map((t) => {
                  const TypeTag = t.link ? "a" : "div";
                  const typeProps = t.link
                    ? { href: t.link, target: "_blank", rel: "noopener noreferrer" }
                    : {};
                  const metaLine = [t.jurisdiction, t.dealClosed || t.year]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <article className="tx-card" key={t.id}>
                      {(t.sector || t.jurisdiction) && (
                        <div className="tx-sector">{t.sector || t.jurisdiction}</div>
                      )}
                      {(t.type || t.structure) && (
                        <TypeTag className="tx-type" {...typeProps}>
                          {t.type || t.structure}{t.link ? " ↗" : ""}
                        </TypeTag>
                      )}
                      {t.amount && <div className="tx-amount">{t.amount}</div>}
                      <div className="tx-meta">
                        <span className="geo">{metaLine}</span>
                        {t.status && (
                          <span className={`status-pill ${statusClass(t.status)}`}>
                            {t.status}
                          </span>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
              <p className="indicative spacer-m">
                {source === "cms"
                  ? `Showing ${list.length} transaction${list.length === 1 ? "" : "s"} from the Daksfirst transaction ledger. Full track record and current pipeline available in the data room subject to NDA.`
                  : "Selected transactions only. Full track record and current pipeline available in the data room subject to NDA."}
              </p>
            </>
          )}
        </div>
      </section>

      {/* ASSET CLASSES */}
      <section className="funds-section">
        <div className="wrap">
          <div className="eyebrow">Asset classes</div>
          <h2 style={{ color: "var(--white)", marginTop: 14, marginBottom: 28 }}>
            Sectors and structures the team has executed against.
          </h2>
          <div className="classes">
            {assetClasses.map((c) => <span className="chip" key={c}>{c}</span>)}
          </div>
          <p className="indicative">Indicative list. Other structures considered subject to mandate fit.</p>
        </div>
      </section>

      {/* INDICATIVE PARAMETERS */}
      <section>
        <div className="wrap">
          <div className="product-grid">
            <div>
              <div className="eyebrow">Indicative parameters</div>
              <h2 style={{ color: "var(--white)", marginTop: 14, maxWidth: "20ch" }}>
                Typical credit-solution profile.
              </h2>
              <p className="sub spacer-s">
                A snapshot of the parameters Daksfirst typically deploys against. Ranges are
                indicative; transaction terms are agreed deal by deal.
              </p>
            </div>
            <div>
              {parameters.map(([k, v]) => (
                <div className="kv" key={k}>
                  <span className="k">{k}</span>
                  <span className="v">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="reg tight">
        <div className="wrap" style={{ gridTemplateColumns: "1fr", textAlign: "center" }}>
          <div className="cell">
            <p className="muted" style={{ color: "var(--navy-70)", fontSize: 15 }}>
              For the full transaction list including current pipeline, please{" "}
              <Link to="/data-room" style={{ color: "var(--navy)", textDecoration: "underline" }}>
                request data room access
              </Link>.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
